import { Injectable, inject, resource, computed, signal } from '@angular/core';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { AuthService } from '../auth/auth.service';
import type { Sensor, SensorHistory } from '../../shared/interfaces';
import type { ChartPeriod } from '../../shared/charts/interfaces/chart-data.interface';

export interface SensorDataState {
  sensors: Sensor[];
  histories: Map<string, SensorHistory[]>;
}

@Injectable({
  providedIn: 'root'
})
export class SensorService {
  private readonly firestore = inject(FIRESTORE_TOKEN);
  private readonly authService = inject(AuthService);

  public readonly siteId = signal<string | null>(null);
  public readonly chartPeriod = signal<ChartPeriod>('1d');

  private readonly sensorDataResource = resource({
    params: () => ({
      orgId: this.authService.userOrganizationId(),
      role: this.authService.userRole(),
      siteId: this.siteId(),
      period: this.chartPeriod()
    }),
    loader: async ({ params: { orgId, role, siteId, period } }) => {
      if (!siteId) {
        return { sensors: [], histories: new Map<string, SensorHistory[]>() };
      }
      // super_admin 以外は orgId が必須
      if (!orgId && role !== 'super_admin') {
        return { sensors: [], histories: new Map<string, SensorHistory[]>() };
      }

      try {
        const sensorsRef = collection(this.firestore, 'sensors');
        let sensorsQuery;
        if (role === 'super_admin') {
          // super_admin は organizationId フィルタなしで取得
          sensorsQuery = query(
            sensorsRef,
            where('siteId', '==', siteId),
            where('deletedAt', '==', null)
          );
        } else {
          sensorsQuery = query(
            sensorsRef,
            where('organizationId', '==', orgId),
            where('siteId', '==', siteId),
            where('deletedAt', '==', null)
          );
        }
        
        const sensorsSnapshot = await getDocs(sensorsQuery);
        const sensors = sensorsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Sensor));

        if (sensors.length === 0) {
          return { sensors, histories: new Map<string, SensorHistory[]>() };
        }

        const now = new Date();
        const fromDate = new Date(now);
        if (period === '1d') {
          fromDate.setDate(now.getDate() - 1);
        } else if (period === '1w') {
          fromDate.setDate(now.getDate() - 7);
        } else if (period === '1m') {
          fromDate.setDate(now.getDate() - 30);
        }

        // MQTT は date フィールドを "YYYY-MM-DD HH:mm:ss" 形式の文字列で保存しているため、
        // 同形式の文字列で比較する（辞書順比較で正しく機能する）
        const fromDateStr = this.formatDateString(fromDate);

        const historiesMap = new Map<string, SensorHistory[]>();
        
        await Promise.all(sensors.map(async (sensor) => {
          const historyRef = collection(this.firestore, `sensors/${sensor.id}/history`);
          const historyQuery = query(
            historyRef,
            where('date', '>=', fromDateStr),
            orderBy('date', 'desc'),
            limit(1000)
          );
          
          const historySnapshot = await getDocs(historyQuery);
          const histories = historySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as SensorHistory));
          
          historiesMap.set(sensor.id, histories);
        }));

        return { sensors, histories: historiesMap };
      } catch (err) {
        console.error('センサーデータの取得に失敗しました:', err);
        throw new Error('センサーデータの取得に失敗しました', { cause: err });
      }
    }
  });

  public readonly sensors = computed(() => this.sensorDataResource.value()?.sensors ?? []);
  public readonly histories = computed(() => this.sensorDataResource.value()?.histories ?? new Map<string, SensorHistory[]>());
  
  public readonly isLoading = computed(() => 
    this.sensorDataResource.status() === 'loading' || 
    this.sensorDataResource.status() === 'reloading'
  );

  public readonly error = computed(() => {
    const err = this.sensorDataResource.error();
    return err instanceof Error ? err.message : null;
  });
  
  public readonly sensorsByCategory = computed(() => {
    return this.sensors().reduce((acc, sensor) => {
      const category = sensor.categoryName.toLowerCase() as keyof typeof acc;
      if (acc[category]) {
        acc[category].push(sensor);
      }
      return acc;
    }, {
      wbgt: [] as Sensor[],
      wind: [] as Sensor[],
      noise: [] as Sensor[],
      ph: [] as Sensor[],
      tilt: [] as Sensor[],
    });
  });

  /**
   * Date オブジェクトを MQTT 互換の "YYYY-MM-DD HH:mm:ss" 形式文字列に変換する。
   * タイムゾーンは Asia/Tokyo (JST) を使用する。
   */
  private formatDateString(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    // JST (UTC+9) に変換
    const jst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
    return `${jst.getUTCFullYear()}-${pad(jst.getUTCMonth() + 1)}-${pad(jst.getUTCDate())} ${pad(jst.getUTCHours())}:${pad(jst.getUTCMinutes())}:${pad(jst.getUTCSeconds())}`;
  }
}
